/* eslint-disable */

import { isEqual } from "lodash";
import { Path } from "./Path";

/**
 * Describes a contiguous range of text.
 *
 * e.g. The TextRange {offset: 3, length: 4}
 * for the text "hello world" would return "lo w".
 * ```
 *hello world
 *   ~~~~ -> length === 4
 *---- -> offset === 3
 * ```
 */
export interface TextRange {
  offset: number;
  length: number;
}

/// See https://miro.com/app/board/o9J_l9D4smw=/
export interface TextInNodeLocation {
  /// The path to the text node
  index: Path;

  /// Within this text node, the character offset
  offset: number;
}

/**
 * Describes a range within a tree structure, specifically
 * the text nodes within a tree.
 *
 * This interface is meant to be used with the web
 * Range API https://developer.mozilla.org/en-US/docs/Web/API/Range
 */
export interface TreeRange {
  start: TextInNodeLocation;
  end: TextInNodeLocation;
}

/**
 * Represents a change that can be applied to a tree range.
 */
export interface Delta {
  /**
   * The shift applied to the last index
   */
  lastIndex: number;

  /**
   * The shift applied to the offset
   */
  offset: number;
}

export interface TreeRangeDelta {
  /**
   * The range to apply the delta to
   */
  range: TreeRange;

  /**
   * A change to a given range. If null, it's meant to be removed.
   */
  delta: Delta | null;
}
/**
 * @param index the index to which both the start and end belong
 * @param startOffset start offset
 * @param endOffset end offset
 * @returns a tree range with index as the start and end index, and the corresponding offsets.
 */
export function singularTreeRange(
  index: number[],
  startOffset: number,
  endOffset: number
): TreeRange {
  return {
    start: {
      index,
      offset: startOffset,
    },
    end: {
      index,
      offset: endOffset,
    },
  };
}

/**
 * @param treeRange a range to convert to a text range
 * @returns a text range within the tree range iff the range is within the same index,
 * null otherwise.
 */
export function toTextRange(treeRange: TreeRange): TextRange | null {
  const { start, end } = treeRange;
  if (!isEqual(start.index, end.index)) {
    return null;
  }
  return {
    offset: start.offset,
    length: end.offset - start.offset,
  };
}

type Order = "BEFORE" | "AT" | "AFTER";

/**
 * @param rangeA
 * @param rangeB
 * @returns true iff locationA is before locationB, assuming they are part of the same tree.
 * More formally, the start of locationA is before locationB iff
 *  - locationB is a subpath of locationA, and locationA is closer to the root, or
 *  - locationA and locationB have the same path (index), and locationA has a lower offset, or
 *  - locationA differs from locationB at some point in their paths, and locationA's ancestor is
 *  before locationB's ancestor in the tree.
 */
export function getLocationOrder(
  locationA: TextInNodeLocation,
  locationB: TextInNodeLocation
): Order {
  const a = locationA;
  const b = locationB;
  const min = Math.min(a.index.length, b.index.length);
  let i = -1;

  do {
    i += 1;
  } while (i < min && a.index[i] === b.index[i]);

  if (i === min) {
    // i indicates that we've reached a leaf of one of the start paths.

    if (a.index.length === b.index.length) {
      // Same start indexes, go by the offset
      if (a.offset < b.offset) {
        return "BEFORE";
      }
      if (a.offset > b.offset) {
        return "AFTER";
      }
      return "AT";
    }
    // Shouldn't really happen if we're only looking at leaf nodes,
    // but "higher" nodes in the tree are considered "before"
    return a.index.length < b.index.length ? "BEFORE" : "AFTER";
  }

  // i points to the node in the path at which startA and startB diverged
  if (a.index[i] < b.index[i]) {
    return "BEFORE";
  }
  if (a.index[i] > b.index[i]) {
    return "AFTER";
  }
  return "AT";
}

// TODO(document): this is inclusive I think
export function locationIntersectsRange(
  location: TextInNodeLocation,
  range: TreeRange
): boolean {
  const { start, end } = range;

  // make sure the location's index is within the start and end
  for (let i = 0; i < location.index.length; i += 1) {
    const currentLevel = location.index[i];

    const leftBoundary = start.index[i];
    const rightBoundary = end.index[i];

    const currentLevelIsOutsideBoundary =
      currentLevel < leftBoundary || rightBoundary < currentLevel;
    if (currentLevelIsOutsideBoundary) {
      return false;
    }
  }
  // Postcondition: location's index points to a text node that is within the range
  const lastLevel = location.index[location.index.length - 1];

  const locationIsWithinStart =
    start.index[start.index.length - 1] === lastLevel;
  const locationIsWithinEnd = end.index[end.index.length - 1] === lastLevel;

  if (locationIsWithinStart && locationIsWithinEnd) {
    return start.offset <= location.offset && location.offset <= end.offset;
  }
  if (locationIsWithinStart) {
    return start.offset <= location.offset;
  }
  if (locationIsWithinEnd) {
    return location.offset <= end.offset;
  }
  return true;
}

/**
 * @param path
 * @param range
 * @returns true iff the path is between inclusive the start and end paths of the range
 */
export function pathIntersectsRange(path: Path, range: TreeRange): boolean {
  // eslint-disable-next-line unicorn/no-for-loop
  for (let i = 0; i < path.length; i += 1) {
    const leftBoundary = range.start.index[i];
    const rightBoundary = range.end.index[i];

    if (path[i] < leftBoundary || rightBoundary < path[i]) {
      return false;
    }
  }
  return true;
}

export function isSubpath(subpath: Path, superpath: Path): boolean {
  // eslint-disable-next-line unicorn/no-for-loop
  for (let i = 0; i < superpath.length; i += 1) {
    if (superpath[i] !== subpath[i]) {
      return false;
    }
  }
  return true;
}

export interface PathChange {
  path: Path;
  type: "ADDED" | "REMOVED";
}

/**
 * Shifts (or keeps the same) a path based on the removal or addition of another path,
 * depending on if it's affect or not. Returns null if the path or its ancestor was removed.
 *
 * The two paths are assumed to be part of the same tree.
 *
 * This function assumes that additions move originals in that spot over to the right,
 * that is they increment the index at that level.
 *
 * @param original a path that might be affected by the modification
 * @param change a path that is added or removed, which might affect the original.
 * @returns the new path that the maybeAffectedPath should be after the change. Could be
 * the same path if it's not affected, or null if the path is removed.
 */
export function pathAfterChange(
  original: Path,
  change: PathChange
): Path | null {
  const newPath = [...original];

  // The changed path is down further in the tree, we don't care
  if (change.path.length > original.length) {
    return newPath;
  }

  let level = 0;
  while (level < change.path.length && level < original.length) {
    // If we've detected the original is before the changed path, we don't care
    if (original[level] < change.path[level]) {
      return newPath;
    }

    level += 1;
  }
  // Move the level back to the last one we checked
  level -= 1;

  const isAncestorOrSelf = change.path.length <= original.length;
  const ancestorOrSelfIsRemoved =
    isAncestorOrSelf && original[level] === change.path[level];
  if (change.type === "REMOVED" && ancestorOrSelfIsRemoved) {
    return null;
  }

  let diff = 0;
  if (change.type === "ADDED") {
    diff = 1;
  } else if (change.type === "REMOVED") {
    diff = -1;
  }
  newPath[level] += diff;

  return newPath;
}

export function findDomNode(root: Node, path: Path): Node | null {
  let currentNode: Node = root;
  for (const i of path) {
    currentNode = currentNode.childNodes.item(i);
  }
  return currentNode;
}

/**
 * Converts a TreeRange into a Range (which is used in the browser)
 */
export function toDomRange(root: Node, treeRange: TreeRange): Range | null {
  root.normalize();
  const { start, end } = treeRange;

  const range = new Range();

  const startNode = findDomNode(root, start.index);
  if (startNode === null) {
    return null;
  }
  range.setStart(startNode, start.offset);

  const endNode = findDomNode(root, end.index);
  if (endNode === null) {
    return null;
  }

  range.setEnd(endNode, end.offset);

  return range;
}
